package co.diena.everything.app.worker.util

import co.touchlab.kermit.Logger
import com.github.michaelbull.result.get
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Deferred
import kotlinx.coroutines.async
import kotlinx.coroutines.delay
import org.lortex.lines.common.AnyResult
import org.lortex.lines.common.kache.InMemoryKache
import org.lortex.lines.common.std.Either
import kotlin.time.Duration
import kotlin.time.Duration.Companion.seconds

interface Loader<K, V> {
    suspend fun load(key: K): AnyResult<V>
}

private val log = Logger.withTag("CachedLoader")

class CachedLoader<K : Any, V : Any>(
    private val loader: Loader<K, V>,
    private val waitBeforeLoading: Duration = 1.seconds,
) {
    val cache = InMemoryKache<K, V>(maxSize = 20 * 1024 * 1024)
    private val loadingMap = mutableMapOf<K, Deferred<AnyResult<V>>>()

    fun setActive(keys: Set<K>) {
        val toCancel = loadingMap - keys
        loadingMap -= toCancel.keys
        toCancel.values.forEach { it.cancel() }
    }

    suspend fun getValue(
        key: K,
        coroutineScope: CoroutineScope,
    ): Either<V, Deferred<AnyResult<V>>> {
        val cacheEntry = cache.get(key)
        return if (cacheEntry != null) {
            Either.Left(cacheEntry)
        } else {
            val loadingValue = loadingMap[key]
            if (loadingValue != null) {
                Either.Right(loadingValue)
            } else {
                val job = coroutineScope.async {
                    delay(waitBeforeLoading)
                    loader.load(key)
                        .also {
                            it.get()?.let {
                                cache.put(key, it)
                            }
                        }
                }
                job.invokeOnCompletion {
                    loadingMap.remove(key)?.cancel()
                }
                loadingMap[key] = job
                Either.Right(job)
            }
        }
    }
}